package ai.wagemix.logic

import ai.wagemix.data.WMAuthSyncDataSource
import ai.wagemix.data.WMAuthSyncEvent
import ai.wagemix.data.WMBillingSyncDataSource
import ai.wagemix.data.WMBillingSyncEvent
import ai.wagemix.data.WMCache
import ai.wagemix.data.WMJobDescriptionSyncDataSource
import ai.wagemix.data.WMJobDescriptionSyncEvent
import ai.wagemix.data.WMLocalContext
import ai.wagemix.data.WMLocalContextDataSource
import ai.wagemix.data.WMMixSyncDataSource
import ai.wagemix.data.WMMixSyncEvent
import ai.wagemix.data.WMStatsSyncDataSource
import ai.wagemix.data.WMStatsSyncEvent
import ai.wagemix.data.touchesUser
import ai.wagemix.logic.account.WMAccountDataGateway
import ai.wagemix.logic.appshell.WMAppShellDataGateway
import ai.wagemix.logic.auth.WMAuthDataGateway
import ai.wagemix.logic.picker.WMOccupationPickerDataGateway
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsDataGateway
import ai.wagemix.logic.marketing.WMMarketingChromeDataGateway
import ai.wagemix.logic.mixbuilder.WMMixBuilderDataGateway
import ai.wagemix.logic.mixes.WMSavedMixesDataGateway
import ai.wagemix.logic.payment.WMPaymentResultDataGateway
import ai.wagemix.logic.pricing.WMPricingDataGateway
import ai.wagemix.logic.share.WMShareMixDataGateway
import ai.wagemix.logic.sharedlinks.WMSharedLinksDataGateway
import ai.wagemix.logic.sharedmix.WMSharedMixDataGateway
import ai.wagemix.net.client.WMOccupationStatsCollectionRequestBuilder
import ai.wagemix.net.models.WMArea
import ai.wagemix.net.models.WMIndustry
import ai.wagemix.net.models.WMMixFilters
import ai.wagemix.net.models.WMOccupationTaxonomy
import ai.wagemix.net.models.WMSession
import ai.wagemix.net.models.WMSharedMixCreate
import ai.wagemix.net.models.WMStoredMix
import ai.wagemix.net.models.WMSubscriptionInfo
import ai.wagemix.net.models.WMUpload
import ai.wagemix.net.models.WMUploadFile
import ai.wagemix.net.models.firebase.WMFirebaseMix
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.firebase.WMUserData
import ai.wagemix.net.models.firebase.entitledPlanOf
import com.fsryan.foundation.data.FSCachedValue
import com.fsryan.foundation.data.FSLocalCacheDataSource
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart

/*
 * The gateway role mixins (data-gateway-split playbook, Step 2) and the aggregate (Step 4).
 * Per-presenter gateways live beside their presenters and extend the mixins they need. Only the
 * aggregate has a factory and an implementation; every member is one line of delegation.
 */

/** The feature's update flows, the two context snapshots, the signed-in user, and the per-screen parameter slot. */
interface WMBaseDataGateway {
    val contextUpdateFlow: SharedFlow<WMLocalContext.Update>
    val cacheUpdateFlow: SharedFlow<FSLocalCacheDataSource.Update<WMCache.Value>>
    suspend fun retrieveGlobalContext(): WMLocalContext
    suspend fun retrieveUserContext(uid: String?): WMLocalContext
    suspend fun retrieveActiveUser(): WMCache.Value.User?
    suspend fun upsertScreenParams(uid: String?, screen: String, params: Map<String, String>)
    suspend fun deleteScreenParams(uid: String?, screen: String)
}

/** Shared derivations on the narrowest mixin (split playbook, Step 5). */
suspend fun WMBaseDataGateway.session(): WMSession? = retrieveActiveUser()?.session
suspend fun WMBaseDataGateway.userData(): WMUserData? = retrieveActiveUser()?.userData
suspend fun WMBaseDataGateway.subscription(): WMSubscriptionInfo? = retrieveActiveUser()?.subscription

/**
 * The plan the signed-in account may use now ([entitledPlanOf]): `none` when nobody is signed in or the subscription
 * lapsed. Every [WMPlanRules] check takes this; [subscription] keeps the plan a lapsed account would renew.
 */
suspend fun WMBaseDataGateway.entitledPlan(): WMSubscriptionType {
    val user = retrieveActiveUser()
    return entitledPlanOf(status = user?.userData?.status, plan = user?.subscription?.plan)
}

/** Whether the signed-in account has lapsed ([WMPlanRules.isLapsed]); `false` when nobody is signed in. */
suspend fun WMBaseDataGateway.isLapsed(): Boolean = WMPlanRules.isLapsed(status = userData()?.status)

/**
 * The paid plan a lapsed account would renew: the plan it lapsed from, which the webhook keeps. `null` when the
 * account has not lapsed, or lapsed before the plan was kept.
 */
suspend fun WMBaseDataGateway.lapsedPlan(): WMSubscriptionType? {
    if (!isLapsed()) {
        return null
    }
    return subscription()?.plan?.takeUnless { it == WMSubscriptionType.None }
}

/** [isLapsed] now and after every change to the signed-in user, without repeats. */
fun WMBaseDataGateway.lapsedFlow(): Flow<Boolean> = cacheUpdateFlow
    .filter { it.touchesUser() }
    .map { isLapsed() }
    .onStart { emit(isLapsed()) }
    .distinctUntilChanged()

/** `true` when [update] is about the context of the user [uid] (or the anonymous context when `uid == null`). */
fun WMLocalContext.Update.isForUser(uid: String?): Boolean = contextId == WMLocalContextDataSource.userContextId(uid)

/** `true` when [update] is about the process-wide context. */
val WMLocalContext.Update.isGlobal: Boolean get() = contextId == WMLocalContextDataSource.GLOBAL_CONTEXT_ID

/** The draft, the chart preferences, the reference lists and the stats syncs. */
interface WMMixDataGateway: WMBaseDataGateway {
    val statsSyncEventFlow: SharedFlow<WMStatsSyncEvent<*>>
    suspend fun retrieveMixDraft(): WMCache.Value.MixDraft?
    suspend fun upsertMixDraft(draft: WMCache.Value.MixDraft)
    suspend fun modifyMixDraft(modify: (WMCache.Value.MixDraft?) -> WMCache.Value.MixDraft?)
    suspend fun deleteMixDraft()
    suspend fun retrieveChartPrefs(): WMCache.Value.ChartPrefs?
    suspend fun upsertChartPrefs(prefs: WMCache.Value.ChartPrefs)
    suspend fun retrieveTaxonomy(): FSCachedValue<WMOccupationTaxonomy>?
    suspend fun retrieveAreas(): FSCachedValue<List<WMArea>>?
    suspend fun retrieveIndustries(): FSCachedValue<List<WMIndustry>>?
    fun syncStats(additionalContext: Map<String, Any> = emptyMap(), modify: WMOccupationStatsCollectionRequestBuilder.() -> Unit)
    fun syncTaxonomy(additionalContext: Map<String, Any> = emptyMap())
    fun syncAreas(occupationIds: Set<Int>, additionalContext: Map<String, Any> = emptyMap())
    fun syncIndustries(occupationIds: Set<Int>, additionalContext: Map<String, Any> = emptyMap())
}

/** The mix sync events, shared by every surface that writes mixes, shares them or reads a public one. */
interface WMMixSyncEventsDataGateway: WMBaseDataGateway {
    val mixSyncEventFlow: SharedFlow<WMMixSyncEvent<*>>
}

/** The user's stored mixes (mirrored into the user's context), the selected mix, and the write syncs. */
interface WMStoredMixesDataGateway: WMMixSyncEventsDataGateway {
    suspend fun upsertSelectedMixId(uid: String?, mixId: String)
    suspend fun deleteSelectedMixId(uid: String?)
    fun saveMix(
        uid: String,
        title: String,
        occupations: List<WMFirebaseMix.Occupation>,
        filters: WMMixFilters,
        additionalContext: Map<String, Any> = emptyMap()
    )
    fun updateMix(
        uid: String,
        mixId: String,
        title: String,
        occupations: List<WMFirebaseMix.Occupation>,
        filters: WMMixFilters,
        additionalContext: Map<String, Any> = emptyMap()
    )
    fun renameMix(uid: String, mixId: String, title: String, additionalContext: Map<String, Any> = emptyMap())
    fun duplicateMix(uid: String, mixId: String, title: String, additionalContext: Map<String, Any> = emptyMap())
    fun deleteMix(uid: String, mixId: String, additionalContext: Map<String, Any> = emptyMap())
}

/** The mirrored stored mixes of [uid], re-emitted whenever the mirror changes; empty until the first mirror arrives. */
internal fun WMBaseDataGateway.storedMixesFlow(uid: String?): Flow<List<WMStoredMix>> = contextUpdateFlow
    .filter { it.isForUser(uid) && it.storedMixesChanged() }
    .map { it.after.storedMixes() ?: emptyList() }
    .onStart { emit(retrieveUserContext(uid).storedMixes() ?: emptyList()) }

/** A one-shot read of one mirrored stored mix. */
internal suspend fun WMBaseDataGateway.retrieveStoredMix(uid: String?, mixId: String): WMStoredMix? {
    return retrieveUserContext(uid).storedMixes()?.firstOrNull { it.id == mixId }
}

/** The job-description uploads (mirrored into the user's context) and the pipeline syncs. */
interface WMUploadsDataGateway: WMBaseDataGateway {
    val jobDescriptionSyncEventFlow: SharedFlow<WMJobDescriptionSyncEvent<*>>
    fun uploadJobDescription(uid: String, eventStreamId: String, file: WMUploadFile, additionalContext: Map<String, Any> = emptyMap())
    fun uploadJobDescriptionUrl(uid: String, eventStreamId: String, url: String, additionalContext: Map<String, Any> = emptyMap())
    fun reevaluateMix(uid: String, eventStreamId: String, mixId: String, additionalContext: Map<String, Any> = emptyMap())
    /** Runs a failed upload's pipeline again; the mirrored upload shows the progress. */
    fun retryUpload(uid: String, uploadId: String, additionalContext: Map<String, Any> = emptyMap())
    /** Deletes a failed upload and its files; the mirrored uploads drop it. */
    fun deleteUpload(uid: String, uploadId: String, additionalContext: Map<String, Any> = emptyMap())
    fun fetchFileUrls(uid: String, mixIds: Set<String>, additionalContext: Map<String, Any> = emptyMap())
}

/** The mirrored uploads of [uid], re-emitted whenever the mirror changes; empty until the first mirror arrives. */
internal fun WMBaseDataGateway.uploadsFlow(uid: String?): Flow<List<WMUpload>> = contextUpdateFlow
    .filter { it.isForUser(uid) && it.uploadsChanged() }
    .map { it.after.uploads() ?: emptyList() }
    .onStart { emit(retrieveUserContext(uid).uploads() ?: emptyList()) }

/** The authentication syncs every authenticated surface may start. */
interface WMSessionDataGateway: WMBaseDataGateway {
    val authSyncEventFlow: SharedFlow<WMAuthSyncEvent<*>>
    fun signIn(email: String, password: String, additionalContext: Map<String, Any> = emptyMap())
    fun register(firstName: String, lastName: String, email: String, password: String, additionalContext: Map<String, Any> = emptyMap())
    fun sendEmailVerification(additionalContext: Map<String, Any> = emptyMap())
    fun sendPasswordReset(email: String, additionalContext: Map<String, Any> = emptyMap())
    fun verifyPasswordResetCode(code: String, additionalContext: Map<String, Any> = emptyMap())
    fun confirmPasswordReset(code: String, newPassword: String, additionalContext: Map<String, Any> = emptyMap())
    fun applyActionCode(code: String, additionalContext: Map<String, Any> = emptyMap())
    fun signOut(additionalContext: Map<String, Any> = emptyMap())
    fun updateProfile(uid: String, firstName: String, lastName: String, additionalContext: Map<String, Any> = emptyMap())
}

/** The Stripe syncs; redirect URLs land in the global context and are deleted once followed. */
interface WMBillingDataGateway: WMBaseDataGateway {
    val billingSyncEventFlow: SharedFlow<WMBillingSyncEvent<*>>
    fun fetchPrices(additionalContext: Map<String, Any> = emptyMap())
    fun startCheckout(lookupKey: String, additionalContext: Map<String, Any> = emptyMap())
    fun openBillingPortal(returnPath: String?, additionalContext: Map<String, Any> = emptyMap())
    fun fetchCheckoutSummary(sessionId: String, additionalContext: Map<String, Any> = emptyMap())
    suspend fun deleteCheckoutUrl(lookupKey: String)
    suspend fun deleteBillingPortalUrl()
}

/** Sharing a mix. */
interface WMSharingDataGateway: WMMixSyncEventsDataGateway {
    fun createShare(uid: String, request: WMSharedMixCreate, additionalContext: Map<String, Any> = emptyMap())
    suspend fun deleteShareResult(uid: String?, mixId: String)
    /** Asks for [sharedMixId]'s preview image again; the user's mirrored shares carry the outcome. */
    fun regenerateSharePreview(uid: String, sharedMixId: String, additionalContext: Map<String, Any> = emptyMap())
    /** Turns the user's share link on or off; the mirrored shares carry the outcome. */
    fun setShareActive(uid: String, sharedMixId: String, active: Boolean, additionalContext: Map<String, Any> = emptyMap())
}

/** The public shared-mix document, mirrored into the global context while observed. */
interface WMPublicMixDataGateway: WMMixSyncEventsDataGateway {
    fun observeSharedMix(sharedMixId: String, additionalContext: Map<String, Any> = emptyMap())
    fun stopObservingSharedMix(sharedMixId: String)
}

/** The occupation picker's transient selection and expansion, kept in the user's context. */
interface WMOccupationPickerStateDataGateway: WMBaseDataGateway {
    suspend fun upsertPickerSelection(uid: String?, selection: Set<Int>)
    suspend fun deletePickerSelection(uid: String?)
    suspend fun upsertPickerExpansion(uid: String?, expanded: Set<Int>)
    suspend fun deletePickerExpansion(uid: String?)
}

/**
 * The one Wagemix gateway for the process. Presenters depend on their own sub-interface; the app
 * binds every sub-interface to this one instance.
 */
interface WMDataGateway:
    WMAppShellDataGateway,
    WMMixBuilderDataGateway,
    WMOccupationPickerDataGateway,
    WMSavedMixesDataGateway,
    WMJobDescriptionsDataGateway,
    WMShareMixDataGateway,
    WMAccountDataGateway,
    WMPricingDataGateway,
    WMPaymentResultDataGateway,
    WMAuthDataGateway,
    WMSharedMixDataGateway,
    WMSharedLinksDataGateway,
    WMMarketingChromeDataGateway

fun WMDataGateway(
    localContext: WMLocalContextDataSource,
    cache: WMCache,
    statsSync: WMStatsSyncDataSource,
    jobDescriptionSync: WMJobDescriptionSyncDataSource,
    mixSync: WMMixSyncDataSource,
    billingSync: WMBillingSyncDataSource,
    authSync: WMAuthSyncDataSource
): WMDataGateway = WMDataGatewayImpl(
    localContext = localContext,
    cache = cache,
    statsSync = statsSync,
    jobDescriptionSync = jobDescriptionSync,
    mixSync = mixSync,
    billingSync = billingSync,
    authSync = authSync
)

private class WMDataGatewayImpl(
    private val localContext: WMLocalContextDataSource,
    private val cache: WMCache,
    private val statsSync: WMStatsSyncDataSource,
    private val jobDescriptionSync: WMJobDescriptionSyncDataSource,
    private val mixSync: WMMixSyncDataSource,
    private val billingSync: WMBillingSyncDataSource,
    private val authSync: WMAuthSyncDataSource
): WMDataGateway {

    // WMBaseDataGateway
    override val contextUpdateFlow: SharedFlow<WMLocalContext.Update> get() = localContext.updateFlow
    override val cacheUpdateFlow: SharedFlow<FSLocalCacheDataSource.Update<WMCache.Value>> get() = cache.updateFlow
    override suspend fun retrieveGlobalContext(): WMLocalContext = localContext.retrieveGlobal()
    override suspend fun retrieveUserContext(uid: String?): WMLocalContext = localContext.retrieveUser(uid)
    override suspend fun retrieveActiveUser(): WMCache.Value.User? = cache.retrieveActiveUser()?.data
    override suspend fun upsertScreenParams(uid: String?, screen: String, params: Map<String, String>) {
        localContext.upsertScreenParams(uid, screen, params)
    }
    override suspend fun deleteScreenParams(uid: String?, screen: String) = localContext.deleteScreenParams(uid, screen)

    // WMMixDataGateway
    override val statsSyncEventFlow: SharedFlow<WMStatsSyncEvent<*>> get() = statsSync.eventFlow
    override suspend fun retrieveMixDraft(): WMCache.Value.MixDraft? = cache.retrieveMixDraft()?.data
    override suspend fun upsertMixDraft(draft: WMCache.Value.MixDraft) {
        cache.upsertMixDraft(draft)
    }
    override suspend fun modifyMixDraft(modify: (WMCache.Value.MixDraft?) -> WMCache.Value.MixDraft?) {
        cache.modifyMixDraft(modify)
    }
    override suspend fun deleteMixDraft() {
        cache.deleteMixDraft()
    }
    override suspend fun retrieveChartPrefs(): WMCache.Value.ChartPrefs? = cache.retrieveChartPrefs()?.data
    override suspend fun upsertChartPrefs(prefs: WMCache.Value.ChartPrefs) {
        cache.upsertChartPrefs(prefs)
    }
    override suspend fun retrieveTaxonomy(): FSCachedValue<WMOccupationTaxonomy>? {
        return cache.retrieveTaxonomy()?.let { FSCachedValue(it.data.taxonomy, it.cachedDate) }
    }
    override suspend fun retrieveAreas(): FSCachedValue<List<WMArea>>? {
        return cache.retrieveAreas()?.let { FSCachedValue(it.data.areas, it.cachedDate) }
    }
    override suspend fun retrieveIndustries(): FSCachedValue<List<WMIndustry>>? {
        return cache.retrieveIndustries()?.let { FSCachedValue(it.data.industries, it.cachedDate) }
    }
    override fun syncStats(additionalContext: Map<String, Any>, modify: WMOccupationStatsCollectionRequestBuilder.() -> Unit) {
        statsSync.syncStats(additionalContext, modify)
    }
    override fun syncTaxonomy(additionalContext: Map<String, Any>) = statsSync.syncTaxonomy(additionalContext)
    override fun syncAreas(occupationIds: Set<Int>, additionalContext: Map<String, Any>) = statsSync.syncAreas(occupationIds, additionalContext)
    override fun syncIndustries(occupationIds: Set<Int>, additionalContext: Map<String, Any>) {
        statsSync.syncIndustries(occupationIds, additionalContext)
    }

    // WMMixBuilderDataGateway
    override suspend fun retrieveMixBuilderTutorial(): WMCache.Value.MixBuilderTutorial? {
        return cache.retrieveMixBuilderTutorial()?.data
    }
    override suspend fun modifyMixBuilderTutorial(
        modify: (WMCache.Value.MixBuilderTutorial?) -> WMCache.Value.MixBuilderTutorial?,
    ) {
        cache.modifyMixBuilderTutorial(modify)
    }

    // WMMixSyncEventsDataGateway / WMStoredMixesDataGateway
    override val mixSyncEventFlow: SharedFlow<WMMixSyncEvent<*>> get() = mixSync.eventFlow
    override suspend fun upsertSelectedMixId(uid: String?, mixId: String) = localContext.upsertSelectedMixId(uid, mixId)
    override suspend fun deleteSelectedMixId(uid: String?) = localContext.deleteSelectedMixId(uid)
    override fun saveMix(
        uid: String,
        title: String,
        occupations: List<WMFirebaseMix.Occupation>,
        filters: WMMixFilters,
        additionalContext: Map<String, Any>
    ) = mixSync.saveMix(uid, title, occupations, filters, additionalContext)
    override fun updateMix(
        uid: String,
        mixId: String,
        title: String,
        occupations: List<WMFirebaseMix.Occupation>,
        filters: WMMixFilters,
        additionalContext: Map<String, Any>
    ) = mixSync.updateMix(uid, mixId, title, occupations, filters, additionalContext)
    override fun renameMix(uid: String, mixId: String, title: String, additionalContext: Map<String, Any>) {
        mixSync.renameMix(uid, mixId, title, additionalContext)
    }
    override fun duplicateMix(uid: String, mixId: String, title: String, additionalContext: Map<String, Any>) {
        mixSync.duplicateMix(uid, mixId, title, additionalContext)
    }
    override fun deleteMix(uid: String, mixId: String, additionalContext: Map<String, Any>) = mixSync.deleteMix(uid, mixId, additionalContext)

    // WMUploadsDataGateway
    override val jobDescriptionSyncEventFlow: SharedFlow<WMJobDescriptionSyncEvent<*>> get() = jobDescriptionSync.eventFlow
    override fun uploadJobDescription(uid: String, eventStreamId: String, file: WMUploadFile, additionalContext: Map<String, Any>) {
        jobDescriptionSync.uploadJobDescription(uid, eventStreamId, file, additionalContext)
    }
    override fun uploadJobDescriptionUrl(uid: String, eventStreamId: String, url: String, additionalContext: Map<String, Any>) {
        jobDescriptionSync.uploadJobDescriptionUrl(uid, eventStreamId, url, additionalContext)
    }
    override fun reevaluateMix(uid: String, eventStreamId: String, mixId: String, additionalContext: Map<String, Any>) {
        jobDescriptionSync.reevaluateMix(uid, eventStreamId, mixId, additionalContext)
    }
    override fun retryUpload(uid: String, uploadId: String, additionalContext: Map<String, Any>) {
        jobDescriptionSync.retryUpload(uid, uploadId, additionalContext)
    }
    override fun deleteUpload(uid: String, uploadId: String, additionalContext: Map<String, Any>) {
        jobDescriptionSync.deleteUpload(uid, uploadId, additionalContext)
    }
    override fun fetchFileUrls(uid: String, mixIds: Set<String>, additionalContext: Map<String, Any>) {
        jobDescriptionSync.fetchFileUrls(uid, mixIds, additionalContext)
    }

    // WMSessionDataGateway
    override val authSyncEventFlow: SharedFlow<WMAuthSyncEvent<*>> get() = authSync.eventFlow
    override fun signIn(email: String, password: String, additionalContext: Map<String, Any>) = authSync.signIn(email, password, additionalContext)
    override fun register(firstName: String, lastName: String, email: String, password: String, additionalContext: Map<String, Any>) {
        authSync.register(firstName, lastName, email, password, additionalContext)
    }
    override fun sendEmailVerification(additionalContext: Map<String, Any>) {
        authSync.sendEmailVerification(additionalContext)
    }
    override fun sendPasswordReset(email: String, additionalContext: Map<String, Any>) = authSync.sendPasswordReset(email, additionalContext)
    override fun verifyPasswordResetCode(code: String, additionalContext: Map<String, Any>) {
        authSync.verifyPasswordResetCode(code, additionalContext)
    }
    override fun confirmPasswordReset(code: String, newPassword: String, additionalContext: Map<String, Any>) {
        authSync.confirmPasswordReset(code, newPassword, additionalContext)
    }
    override fun applyActionCode(code: String, additionalContext: Map<String, Any>) = authSync.applyActionCode(code, additionalContext)
    override fun signOut(additionalContext: Map<String, Any>) = authSync.signOut(additionalContext)
    override fun updateProfile(uid: String, firstName: String, lastName: String, additionalContext: Map<String, Any>) {
        authSync.updateProfile(uid, firstName, lastName, additionalContext)
    }

    // WMBillingDataGateway
    override val billingSyncEventFlow: SharedFlow<WMBillingSyncEvent<*>> get() = billingSync.eventFlow
    override fun fetchPrices(additionalContext: Map<String, Any>) = billingSync.fetchPrices(additionalContext)
    override fun startCheckout(lookupKey: String, additionalContext: Map<String, Any>) = billingSync.startCheckout(lookupKey, additionalContext)
    override fun openBillingPortal(returnPath: String?, additionalContext: Map<String, Any>) {
        billingSync.openBillingPortal(returnPath, additionalContext)
    }
    override fun fetchCheckoutSummary(sessionId: String, additionalContext: Map<String, Any>) {
        billingSync.fetchCheckoutSummary(sessionId, additionalContext)
    }
    override suspend fun deleteCheckoutUrl(lookupKey: String) = localContext.deleteCheckoutUrl(lookupKey)
    override suspend fun deleteBillingPortalUrl() = localContext.deleteBillingPortalUrl()

    // WMSharingDataGateway
    override fun createShare(uid: String, request: WMSharedMixCreate, additionalContext: Map<String, Any>) {
        mixSync.createShare(uid, request, additionalContext)
    }
    override suspend fun deleteShareResult(uid: String?, mixId: String) = localContext.deleteShareResult(uid, mixId)
    override fun regenerateSharePreview(uid: String, sharedMixId: String, additionalContext: Map<String, Any>) {
        mixSync.regenerateSharePreview(uid, sharedMixId, additionalContext)
    }
    override fun setShareActive(uid: String, sharedMixId: String, active: Boolean, additionalContext: Map<String, Any>) {
        mixSync.setShareActive(uid, sharedMixId, active, additionalContext)
    }

    // WMPublicMixDataGateway
    override fun observeSharedMix(sharedMixId: String, additionalContext: Map<String, Any>) {
        mixSync.observeSharedMix(sharedMixId, additionalContext)
    }
    override fun stopObservingSharedMix(sharedMixId: String) = mixSync.stopObservingSharedMix(sharedMixId)

    // WMOccupationPickerStateDataGateway
    override suspend fun upsertPickerSelection(uid: String?, selection: Set<Int>) = localContext.upsertPickerSelection(uid, selection)
    override suspend fun deletePickerSelection(uid: String?) = localContext.deletePickerSelection(uid)
    override suspend fun upsertPickerExpansion(uid: String?, expanded: Set<Int>) = localContext.upsertPickerExpansion(uid, expanded)
    override suspend fun deletePickerExpansion(uid: String?) = localContext.deletePickerExpansion(uid)
}
